/**
 * Verify (13-11, widened in 13-11.1): the pending Verification Log rows (D-13-00-3, ISS-168).
 * Built to the approved mock (design/mock-13, Verify tab: lens-aware by VerifierRole; D-13-02-1).
 *
 *   - **Who (D-13-R-6):** Oversight sees every row; a Care Manager, Finance or HR & WHS sees only
 *     the rows whose `VerifierRole` is their role (`mayVerifyEvidence`), so the people E4 nudges
 *     can act. No self-verification, E4 and the completer gate all still apply.
 *   - **Done today (D-13-R-5):** the verdicts given today in this lens (any for Oversight, your
 *     own otherwise), each with "Correct this": a reason, then `vlCorrect` through the writer.
 *     After the day: "Corrections after the day need Oversight in the register".
 *
 *   - A quiet table, `Verdict = pending` by default, oldest first. Filters: card, target system,
 *     action, age (over E4's 5 working days is a gentle amber), verdict.
 *   - Per row: **Verify** (immediate, a quiet tick, then the row fades from the pending view; no
 *     toast undo: a mistake is found again under the Verified filter) and **Reject** (an inline,
 *     required reason, one row at a time).
 *   - **Bulk Verify** of a selection, behind a count confirm with the CardId breakdown; the failed
 *     rows stay selected. There is **no bulk Reject**: each rejection needs its own reason.
 *   - `RecordRef = ActionRegister/<id>`: the linked action shows inline (LinkedAction). When that
 *     action has a completion, Verify / Reject go through `canDoVerb` with the `completedByUpn`
 *     from 13-07's `loadVerifyContext` (ISS-188): an unknown completer is refused with the reason
 *     on screen, never hidden. `Incidents/<id>` links to the Closure trail page.
 *
 * Writes: `useActionRuntime().writer.verifyVL` / `.verifyVLBatch`, synchronous (no outbox, so
 * ISS-189 is not needed). If a verdict saves but some audit rows don't (`CommitError('audit')`, a
 * bulk `auditPending`, or a correction's), the page owes them and offers "Post the N audit rows"
 * through `writer.postVlAudit`, de-duped on a fresh read (13-13, ISS-194). In dev that writer is the practice-mode one: it builds the exact patch
 * through lib/vlVerbs, logs it, and saves nothing. Only the five permitted VL fields ever change.
 *
 * `VerifyPage` wires the runtime (Graph reads, the writer, the actor) into `VerifyBoard`, which
 * takes a `VerifySource` so it can be viewed with synthetic data (design/verify-preview).
 *
 * **Incidents to close (13.2-08, D-13.2-P-8).** For Oversight, a section above the Verification Log lists every
 * incident a Care Manager has readied, each with its verification checklist (`IncidentsToClose`, fed by
 * `useIncidentsToClose`). The Verify count in the app bar then adds the incidents ready for this person to close
 * (every check passes, and someone else readied it), so the badge means "ready for you". Other roles see the board
 * exactly as before.
 */

import { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { MouseEvent as ReactMouseEvent, ReactNode } from 'react';
import {
  Button,
  Cell,
  Checkbox,
  Column,
  Dialog,
  Heading,
  Modal,
  ModalOverlay,
  Row,
  Table,
  TableBody,
  TableHeader,
} from 'react-aria-components';
import type { Key, Selection } from 'react-aria-components';
import { Link } from 'react-router-dom';
import { useActionRuntime } from '../../hooks/useActionRuntime';
import { useActor } from '../../hooks/useActor';
import { usePublishNavCount } from '../../hooks/useNavCounts';
import { liveIncidentsToCloseSource, useIncidentsToClose } from '../../hooks/useIncidentsToClose';
import type { IncidentsToCloseSource } from '../../hooks/useIncidentsToClose';
import { roleLabel } from '../../config/nav';
import { mayVerifyEvidence } from '../../lib/actionAuthority';
import { CommitError, listActionRows, listVerificationRows, loadVerifyContext } from '../../services/actionService';
import type { VlAuditPending, VlVerifyVerdict } from '../../services/actionService';
import type { VlBatchSummary, WriteMode } from '../../services/commitAdapter';
import { VerbError } from '../../lib/actionVerbs';
import type { CommitResult } from '../../lib/outbox';
import {
  E4_WINDOW_WORKING_DAYS,
  MIN_REASON_CHARS,
  VL_VERIFIER_ROLE,
  calendarDaysSince,
  correctionRefusal,
  inCorrectionWindow,
  linkedActionGate,
  needsCompleter,
  parseRecordRef,
  refusalText,
  vlQuestion,
  vlVerify,
  workingDaysSince,
} from '../../lib/vlVerbs';
import type { LinkedState, VlCorrectContext, VlCorrectVerdict } from '../../lib/vlVerbs';
import type { ActionRow, Actor, AuditRowDraft, VerificationRow, VerificationVerdict } from '../../types/actionRegister';
import IncidentsToClose from './IncidentsToClose';
import LinkedAction from './LinkedAction';
import styles from './VerifyPage.module.css';

// ---------------------------------------------------------------------------
// The data source (Graph + writer in the app; synthetic in the preview)
// ---------------------------------------------------------------------------

export interface VerifySource {
  mode: WriteMode;
  /** Every Verification Log row (paged through nextLink); the page filters. */
  listRows: () => Promise<VerificationRow[]>;
  /** Every Action Register row: one read serves every linked row. */
  listActions: () => Promise<ActionRow[]>;
  /** Who completed a linked action (ISS-188), from its audit trail. */
  loadCompleter: (row: ActionRow) => Promise<{ completedByUpn?: string }>;
  verify: (row: VerificationRow, verdict: VlVerifyVerdict, opts?: { reason?: string }) => Promise<CommitResult>;
  verifyBatch: (rows: readonly VerificationRow[], verdict: VlVerifyVerdict) => Promise<VlBatchSummary>;
  /** 13-11.1 (D-13-R-5): correct a verdict given today. */
  correct: (row: VerificationRow, verdict: VlCorrectVerdict, reason: string, ctx?: VlCorrectContext) => Promise<CommitResult>;
  /**
   * 13-13 (ISS-194): post audit rows that failed after their verdict saved, de-duped on a fresh
   * read (D-13-07-3). Returns what is still not posted.
   */
  postAudit: (pending: VlAuditPending) => Promise<VlAuditPending>;
}

export default function VerifyPage() {
  const { io, writer, siteError } = useActionRuntime();
  const actorState = useActor();
  const actor = actorState.status === 'ready' ? actorState.actor : null;

  const source = useMemo<VerifySource | null>(() => {
    if (!io || !actor) return null;
    return {
      mode: writer.mode,
      listRows: () => listVerificationRows(io),
      listActions: () => listActionRows(io),
      loadCompleter: async (row) => ({ completedByUpn: (await loadVerifyContext(io, row)).completedByUpn }),
      verify: (row, verdict, opts) => writer.verifyVL(io, row, verdict, actor, opts),
      verifyBatch: (rows, verdict) => writer.verifyVLBatch(io, rows, verdict, actor),
      correct: (row, verdict, reason, ctx) => writer.correctVL(io, row, verdict, reason, actor, ctx),
      postAudit: (pending) => writer.postVlAudit(io, pending),
    };
  }, [io, actor, writer]);

  // 13.2-08: Oversight's "Incidents to close". In practice mode their own practice incidents join the live read.
  const practiceUpn = writer.mode === 'practice' ? (actor?.upn ?? null) : null;
  const closeSource = useMemo<IncidentsToCloseSource | null>(
    () => (io ? liveIncidentsToCloseSource(io, { practiceUpn }) : null),
    [io, practiceUpn],
  );

  return <VerifyBoard source={source} actor={actor} problem={siteError} closeSource={closeSource} />;
}

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

const MELBOURNE = new Intl.DateTimeFormat('en-AU', {
  timeZone: 'Australia/Melbourne',
  weekday: 'short',
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
});

function absolute(iso: string | null): string {
  const t = Date.parse(iso ?? '');
  return Number.isNaN(t) ? '' : MELBOURNE.format(t);
}

function relative(iso: string | null, now: number): string {
  const d = calendarDaysSince(iso, now);
  if (d === null) return 'unknown';
  if (d === 0) return 'today';
  if (d === 1) return 'yesterday';
  return `${d} days ago`;
}

function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`;
}

/** Runs `fn` over `items`, at most `limit` at a time. */
async function eachLimited<T>(items: readonly T[], limit: number, fn: (item: T) => Promise<void>): Promise<void> {
  let next = 0;
  const worker = async () => {
    while (next < items.length) {
      const item = items[next++];
      await fn(item);
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
}

// ---------------------------------------------------------------------------
// State shapes
// ---------------------------------------------------------------------------

interface Base {
  token: number;
  loadedAt: number;
  rows: VerificationRow[];
  actions: Map<number, ActionRow> | 'error';
}

type LoadState =
  | { token: number; status: 'ready'; base: Base }
  | { token: number; status: 'error'; message: string };

type Completer = { completedByUpn?: string } | 'error';

interface Filters {
  card: string;
  target: string;
  action: string;
  age: '' | 'over' | 'within';
  verdict: VerificationVerdict | 'all';
}

const DEFAULT_FILTERS: Filters = { card: '', target: '', action: '', age: '', verdict: 'pending' };

type Settled = 'verified' | 'rejected';

type BulkState =
  | null
  | { phase: 'confirm'; rows: VerificationRow[] }
  | { phase: 'running'; count: number }
  | { phase: 'done'; summary: VlBatchSummary; lines: string[]; headline: string };

/** Audit drafts owed per Verification Log row (ISS-194): the verdict saved, these rows did not. */
type Owed = ReadonlyMap<number, AuditRowDraft[]>;

function owedCount(owed: Owed, ids?: ReadonlySet<number>): number {
  let n = 0;
  for (const [id, drafts] of owed) if (!ids || ids.has(id)) n += drafts.length;
  return n;
}

const NO_LINK: LinkedState = { status: 'none' };

const rowKey = (id: number) => `r:${id}`;
const detailKey = (id: number) => `d:${id}`;

const VERDICT_LABEL: Record<VerificationVerdict, string> = {
  pending: 'Pending',
  verified: 'Verified',
  rejected: 'Rejected',
  'sampled-ok': 'Sampled OK',
  'packet-delivered': 'Packet delivered',
};

const COMPLETER_CONCURRENCY = 4;
/** Rows on screen at a time: React Aria's Table re-renders every row on a selection change. */
const PAGE_SIZE = 50;
const TICK_MS = 900;
const FADE_MS = 320;

// ---------------------------------------------------------------------------
// The board
// ---------------------------------------------------------------------------

interface BoardProps {
  /** null while signing in / resolving the site. */
  source: VerifySource | null;
  /** null until the signed-in person (and their site user id) is known. */
  actor: Actor | null;
  /** The site could not be reached. */
  problem?: string | null;
  /** 13.2-08: Oversight's "Incidents to close" reads (absent: the section is not shown). */
  closeSource?: IncidentsToCloseSource | null;
  /** 13.2-08: the clock the checklist is worked out against (the preview and tests pin it). */
  closeNow?: () => Date;
}

export function VerifyBoard({ source, actor, problem = null, closeSource = null, closeNow }: BoardProps) {
  const toClose = useIncidentsToClose(actor, closeSource, closeNow ? { now: closeNow } : {});
  const [reloadToken, setReloadToken] = useState(0);
  const [load, setLoad] = useState<LoadState | null>(null);
  const [completers, setCompleters] = useState<{ token: number; map: Map<number, Completer> }>({ token: -1, map: new Map() });
  const [patches, setPatches] = useState<{ token: number; map: Map<number, Partial<VerificationRow>> }>({ token: -1, map: new Map() });
  const [filters, setFilters] = useState<Filters>(DEFAULT_FILTERS);
  const [selected, setSelected] = useState<Selection>(new Set<Key>());
  const [expanded, setExpanded] = useState<ReadonlySet<number>>(new Set());
  const [rejectFor, setRejectFor] = useState<number | null>(null);
  const [busy, setBusy] = useState<ReadonlySet<number>>(new Set());
  const [settled, setSettled] = useState<ReadonlyMap<number, Settled>>(new Map());
  const [leaving, setLeaving] = useState<ReadonlySet<number>>(new Set());
  const [notes, setNotes] = useState<ReadonlyMap<number, string>>(new Map());
  const [bulk, setBulk] = useState<BulkState>(null);
  const [anchor, setAnchor] = useState<number | null>(null);
  const [limit, setLimit] = useState(PAGE_SIZE);
  /** Rows whose verdict this session gave: proof of "who gave it" when the site id is unknown. */
  const [given, setGiven] = useState<ReadonlySet<number>>(new Set());
  const [correctFor, setCorrectFor] = useState<number | null>(null);
  /**
   * 13-13 (ISS-194): audit rows still owed after a verdict saved (single, correction or bulk).
   * Shown above the table with "Post the N audit rows" until they post; never dropped quietly.
   */
  const [owed, setOwed] = useState<Owed>(new Map());
  const [posting, setPosting] = useState(false);
  // While audit rows are owed, leaving the page asks first (they live only in this tab).
  useEffect(() => {
    if (owed.size === 0) return;
    const onLeave = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener('beforeunload', onLeave);
    return () => window.removeEventListener('beforeunload', onLeave);
  }, [owed]);
  /** The correction window is a Melbourne day, so the page re-reads the clock once a minute. */
  const [clock, setClock] = useState(() => Date.now());
  useEffect(() => {
    const t = window.setInterval(() => setClock(Date.now()), 60_000);
    return () => window.clearInterval(t);
  }, []);

  // ---- reads -------------------------------------------------------------

  useEffect(() => {
    if (!source) return;
    let cancelled = false;
    const token = reloadToken;
    Promise.all([source.listRows(), source.listActions().catch(() => 'error' as const)])
      .then(([rows, actions]) => {
        if (cancelled) return;
        const map = actions === 'error' ? 'error' : new Map(actions.map((a) => [a.id, a] as const));
        setLoad({ token, status: 'ready', base: { token, loadedAt: Date.now(), rows, actions: map } });
      })
      .catch((err: unknown) => {
        if (!cancelled) setLoad({ token, status: 'error', message: err instanceof Error ? err.message : String(err) });
      });
    return () => {
      cancelled = true;
    };
  }, [source, reloadToken]);

  const base = load?.status === 'ready' && load.token === reloadToken ? load.base : null;
  const stale = load?.status === 'ready' ? load.base : null; // shown while a reload runs
  const shown = base ?? stale;

  // Who completed each linked, completed action (ISS-188): one audit read per action.
  useEffect(() => {
    if (!source || !base || base.actions === 'error') return;
    const actions = base.actions;
    const ids = new Set<number>();
    for (const r of base.rows) {
      const p = parseRecordRef(r.recordRef);
      const a = p.kind === 'action' ? actions.get(p.id) : undefined;
      if (a && needsCompleter(a)) ids.add(a.id);
    }
    let cancelled = false;
    const token = base.token;
    const put = (id: number, c: Completer) =>
      setCompleters((prev) => {
        const map = new Map(prev.token === token ? prev.map : []);
        map.set(id, c);
        return { token, map };
      });
    void eachLimited([...ids], COMPLETER_CONCURRENCY, async (id) => {
      const row = actions.get(id) as ActionRow;
      try {
        const c = await source.loadCompleter(row);
        if (!cancelled) put(id, c);
      } catch {
        if (!cancelled) put(id, 'error');
      }
    });
    return () => {
      cancelled = true;
    };
  }, [source, base]);

  const reload = useCallback(() => setReloadToken((t) => t + 1), []);

  // ---- derived -----------------------------------------------------------

  const now = shown?.loadedAt ?? 0;

  /**
   * The rows this lens verifies (D-13-R-6): every row for Oversight, otherwise only those whose
   * `VerifierRole` is the actor's role. Nobody is shown a row they could not act on.
   */
  const rows = useMemo<VerificationRow[]>(() => {
    if (!shown || !actor) return [];
    const p = patches.token === shown.token ? patches.map : null;
    const list = shown.rows
      .map((r) => (p?.has(r.id) ? { ...r, ...p.get(r.id) } : r))
      .filter((r) => mayVerifyEvidence(r.verifierRole, actor));
    return list.sort((a, b) => (Date.parse(a.writtenAt) || 0) - (Date.parse(b.writtenAt) || 0) || a.id - b.id);
  }, [shown, patches, actor]);

  const oversight = !!actor?.roles.includes('Oversight');
  const correctCtx = useCallback(
    (row: VerificationRow): VlCorrectContext => (actor && given.has(row.id) ? { verdictByUpn: actor.upn } : {}),
    [actor, given],
  );

  const pendingCount = rows.filter((r) => r.verdict === 'pending').length;
  // 13.2-08: Oversight's badge adds the incidents ready for them to close.
  usePublishNavCount('/verify', shown ? pendingCount + (toClose.readyCount ?? 0) : undefined);

  /**
   * The linked Action Register state per Verification Log row, built from the loaded rows (whose
   * RecordRef never changes) so a verify elsewhere does not rebuild it. One object per action.
   */
  const links = useMemo(() => {
    const byRow = new Map<number, LinkedState>();
    if (!shown) return byRow;
    const byAction = new Map<number, LinkedState>();
    const done = completers.token === shown.token ? completers.map : null;
    for (const r of shown.rows) {
      const p = parseRecordRef(r.recordRef);
      if (p.kind !== 'action') continue;
      let link = byAction.get(p.id);
      if (!link) {
        const ar = shown.actions === 'error' ? 'error' : shown.actions.get(p.id);
        if (ar === 'error') link = { status: 'error', ref: p.ref };
        else if (!ar) link = { status: 'missing', ref: p.ref };
        else if (!needsCompleter(ar)) link = { status: 'ready', row: ar, completer: 'not-needed' };
        else link = { status: 'ready', row: ar, completer: done?.get(ar.id) ?? 'loading' };
        byAction.set(p.id, link);
      }
      byRow.set(r.id, link);
    }
    return byRow;
  }, [shown, completers]);

  /** Why each pending row cannot be verified / rejected by this actor right now (null = it can). */
  const refusals = useMemo(() => {
    const out = new Map<number, string | null>();
    if (!actor) return out;
    const at = new Date(now || 0);
    for (const row of rows) {
      if (row.verdict !== 'pending') continue;
      const p = parseRecordRef(row.recordRef);
      const linkedRef = p.kind === 'action' ? p.ref : undefined;
      let why: string | null = null;
      try {
        vlVerify(row, actor, at);
        const gate = linkedActionGate(links.get(row.id) ?? NO_LINK, actor);
        if (!gate.ok) why = refusalText(gate.reason, linkedRef);
      } catch (err) {
        why = refusalText(err instanceof VerbError ? err.code : 'unknown', linkedRef);
      }
      out.set(row.id, why);
    }
    return out;
  }, [rows, links, actor, now]);

  const refusalFor = useCallback((row: VerificationRow) => refusals.get(row.id) ?? null, [refusals]);

  /**
   * Done today (D-13-R-5): verdicts given today in this lens that this actor may correct (any for
   * Oversight; their own otherwise), newest first. Each says why if it can't be corrected.
   */
  const doneToday = useMemo(() => {
    if (!actor) return [];
    const out: Array<{ row: VerificationRow; why: string | null }> = [];
    for (const row of rows) {
      if (row.verdict === 'pending' || !inCorrectionWindow(row, new Date(clock))) continue;
      const ctx = correctCtx(row);
      const refusal = correctionRefusal(row, actor, new Date(clock), ctx);
      if (refusal === 'not-verifier' || refusal === 'no-verdict') continue;
      const p = parseRecordRef(row.recordRef);
      let why: string | null = refusal ? refusalText(refusal) : null;
      if (!why) {
        const gate = linkedActionGate(links.get(row.id) ?? NO_LINK, actor);
        if (!gate.ok) why = refusalText(gate.reason, p.kind === 'action' ? p.ref : undefined);
      }
      out.push({ row, why });
    }
    return out.sort((a, b) => (Date.parse(b.row.verifiedAt ?? '') || 0) - (Date.parse(a.row.verifiedAt ?? '') || 0));
  }, [rows, actor, clock, correctCtx, links]);

  /** Rows with a verdict whose correction window has closed (said in their details). */
  const windowClosed = useMemo(() => {
    const out = new Set<number>();
    for (const r of rows) if (r.verdict !== 'pending' && r.verdict !== 'packet-delivered' && !inCorrectionWindow(r, new Date(clock))) out.add(r.id);
    return out;
  }, [rows, clock]);

  const options = useMemo(() => {
    const uniq = (pick: (r: VerificationRow) => string) => [...new Set(rows.map(pick).filter(Boolean))].sort();
    return { card: uniq((r) => r.cardId), target: uniq((r) => r.targetSystem), action: uniq((r) => r.action) };
  }, [rows]);

  const visible = useMemo(
    () =>
      rows.filter((r) => {
        if (settled.has(r.id)) return true; // the tick plays before the row leaves
        if (filters.verdict !== 'all' && r.verdict !== filters.verdict) return false;
        if (filters.card && r.cardId !== filters.card) return false;
        if (filters.target && r.targetSystem !== filters.target) return false;
        if (filters.action && r.action !== filters.action) return false;
        if (filters.age) {
          const wd = workingDaysSince(r.writtenAt, now) ?? 0;
          if (filters.age === 'over' ? wd <= E4_WINDOW_WORKING_DAYS : wd > E4_WINDOW_WORKING_DAYS) return false;
        }
        return true;
      }),
    [rows, filters, settled, now],
  );

  const onScreen = useMemo(() => visible.slice(0, limit), [visible, limit]);

  const canSelect = useCallback(
    (r: VerificationRow) => !!actor && r.verdict === 'pending' && !busy.has(r.id) && !settled.has(r.id) && refusalFor(r) === null,
    [actor, busy, settled, refusalFor],
  );

  const selectable = useMemo(() => visible.filter(canSelect).map((r) => r.id), [visible, canSelect]);
  const selectedIds = useMemo(
    () => (selected === 'all' ? selectable : selectable.filter((id) => selected.has(rowKey(id)))),
    [selected, selectable],
  );

  const disabledKeys = useMemo(() => {
    const keys: Key[] = [];
    const ok = new Set(selectable);
    for (const r of onScreen) {
      keys.push(detailKey(r.id));
      if (!ok.has(r.id)) keys.push(rowKey(r.id));
    }
    return keys;
  }, [onScreen, selectable]);

  // ---- writes ------------------------------------------------------------

  const setIn = <T,>(set: (fn: (prev: ReadonlySet<T>) => ReadonlySet<T>) => void, id: T, on: boolean) =>
    set((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });

  const note = (id: number, text: string | null) =>
    setNotes((prev) => {
      const next = new Map(prev);
      if (text) next.set(id, text);
      else next.delete(id);
      return next;
    });

  const applyLocal = (token: number, id: number, patch: Partial<VerificationRow>) =>
    setPatches((prev) => {
      const map = new Map(prev.token === token ? prev.map : []);
      map.set(id, { ...map.get(id), ...patch });
      return { token, map };
    });

  /** The quiet tick, then the row fades out of the pending view. */
  const settle = (id: number, kind: Settled) => {
    setSettled((prev) => new Map(prev).set(id, kind));
    setSelected((prev) => {
      if (prev === 'all') return prev;
      const next = new Set(prev);
      next.delete(rowKey(id));
      return next;
    });
    window.setTimeout(() => setIn(setLeaving, id, true), TICK_MS);
    window.setTimeout(() => {
      setSettled((prev) => {
        const next = new Map(prev);
        next.delete(id);
        return next;
      });
      setIn(setLeaving, id, false);
    }, TICK_MS + FADE_MS);
  };

  /** What the builders wrote, mirrored locally (VerifierRole is stamped only when blank, D-13-R-6). */
  const verdictPatch = (row: VerificationRow, verdict: Settled, reason?: string): Partial<VerificationRow> => ({
    verdict,
    verifiedAt: new Date().toISOString(),
    verifierRole: row.verifierRole || VL_VERIFIER_ROLE,
    verifiedByLookupId: actor?.lookupId ?? null,
    ...(reason ? { exceptionReason: reason } : {}),
  });

  /** A correction (vlCorrect): the reason on a reject, cleared on a return to verified. */
  const correctedPatch = (row: VerificationRow, verdict: VlCorrectVerdict, reason: string): Partial<VerificationRow> => ({
    verdict,
    verifiedAt: new Date().toISOString(),
    verifierRole: row.verifierRole || VL_VERIFIER_ROLE,
    verifiedByLookupId: actor?.lookupId ?? null,
    exceptionReason: verdict === 'rejected' ? reason : null,
  });

  const markGiven = (ids: readonly number[]) =>
    setGiven((prev) => {
      const next = new Set(prev);
      for (const id of ids) next.add(id);
      return next;
    });

  /** Add drafts still to post for a row (ISS-194). */
  const owe = (id: number, drafts: readonly AuditRowDraft[]) => {
    if (drafts.length === 0) return;
    setOwed((prev) => new Map(prev).set(id, [...(prev.get(id) ?? []), ...drafts]));
  };

  /**
   * "Post the N audit rows" (ISS-194, mirroring 13-12's retryBulkAudit): one fresh read per row,
   * de-duped on its [a:<id>] tag + FieldChanged; a failed read posts nothing for that row.
   */
  const postOwed = async () => {
    if (!source || posting || owed.size === 0) return;
    const pending: VlAuditPending = [...owed].map(([id, drafts]) => ({ id, drafts }));
    setPosting(true);
    let still: VlAuditPending = pending;
    try {
      still = await source.postAudit(pending);
    } catch {
      // keep them owed; the button stays
    }
    const left = new Map(still.filter((p) => p.drafts.length).map((p) => [p.id, p.drafts] as const));
    setOwed((prev) => {
      // Anything owed since the post started is kept as well.
      const next = new Map(left);
      for (const [id, drafts] of prev) {
        const sent = pending.find((p) => p.id === id)?.drafts ?? [];
        const extra = drafts.filter((d) => !sent.includes(d));
        if (extra.length) next.set(id, [...(next.get(id) ?? []), ...extra]);
      }
      return next;
    });
    for (const { id } of pending) {
      const n = left.get(id)?.length ?? 0;
      note(id, n ? `Saved, but ${plural(n, 'audit row')} still didn't post. Try posting them again.` : 'Saved, and its audit rows are now posted.');
    }
    setPosting(false);
  };

  const explain = (row: VerificationRow, err: unknown): string => {
    const p = parseRecordRef(row.recordRef);
    if (err instanceof VerbError) return refusalText(err.code, p.kind === 'action' ? p.ref : undefined);
    if (err instanceof CommitError && err.statusCode === 412) {
      return 'Someone else changed this row since it loaded, so nothing was saved. The table has been reloaded.';
    }
    const status = err instanceof CommitError && err.statusCode ? ` (${err.statusCode})` : '';
    return `This didn't save${status}. Try again.`;
  };

  const commitOne = async (row: VerificationRow, verdict: Settled, reason?: string) => {
    if (!source || !shown) return;
    const token = shown.token;
    setIn(setBusy, row.id, true);
    note(row.id, null);
    try {
      const res = await source.verify(row, verdict, reason ? { reason } : undefined);
      applyLocal(token, row.id, { ...verdictPatch(row, verdict, reason), eTag: res.eTag ?? row.eTag });
      markGiven([row.id]);
      if (verdict === 'rejected') {
        setRejectFor(null);
      }
      settle(row.id, verdict);
    } catch (err) {
      if (err instanceof CommitError && err.stage === 'audit') {
        // The PATCH landed; some audit rows did not. Say so rather than pretend either way.
        applyLocal(token, row.id, verdictPatch(row, verdict, reason));
        markGiven([row.id]);
        owe(row.id, err.remainingAudit);
        note(row.id, `Saved, but ${plural(err.remainingAudit.length, 'audit row')} didn't post. Post them from the note above the table.`);
        console.warn('[verify] audit rows still to post', { id: row.id, drafts: err.remainingAudit });
        return;
      }
      note(row.id, explain(row, err));
      if (err instanceof CommitError && err.statusCode === 412) reload();
    } finally {
      setIn(setBusy, row.id, false);
    }
  };

  /** Correct a verdict given today (D-13-R-5). Synchronous, through the writer, like verify. */
  const commitCorrect = async (row: VerificationRow, verdict: VlCorrectVerdict, reason: string) => {
    if (!source || !shown) return;
    const token = shown.token;
    setIn(setBusy, row.id, true);
    note(row.id, null);
    const label = verdict === 'rejected' ? 'Rejected' : 'Verified';
    try {
      const res = await source.correct(row, verdict, reason, correctCtx(row));
      applyLocal(token, row.id, { ...correctedPatch(row, verdict, reason), eTag: res.eTag ?? row.eTag });
      markGiven([row.id]);
      setCorrectFor(null);
      note(row.id, `Corrected to ${label}, with your reason in the audit trail.`);
    } catch (err) {
      if (err instanceof CommitError && err.stage === 'audit') {
        applyLocal(token, row.id, correctedPatch(row, verdict, reason));
        markGiven([row.id]);
        setCorrectFor(null);
        owe(row.id, err.remainingAudit);
        note(row.id, `Corrected, but ${plural(err.remainingAudit.length, 'audit row')} didn't post. Post them from the note above the table.`);
        console.warn('[verify] correction audit rows still to post', { id: row.id, drafts: err.remainingAudit });
        return;
      }
      note(row.id, explain(row, err));
      if (err instanceof CommitError && err.statusCode === 412) reload();
    } finally {
      setIn(setBusy, row.id, false);
    }
  };

  const openBulk = () => {
    const ids = new Set(selectedIds);
    setBulk({ phase: 'confirm', rows: rows.filter((r) => ids.has(r.id)) });
  };

  const runBulk = async (toVerify: VerificationRow[]) => {
    if (!source || !shown) return;
    const token = shown.token;
    // Re-check at the moment of the write (a completer may have resolved since selection).
    const go = toVerify.filter((r) => refusalFor(r) === null);
    setBulk({ phase: 'running', count: go.length });
    go.forEach((r) => setIn(setBusy, r.id, true));
    let summary: VlBatchSummary;
    try {
      summary = await source.verifyBatch(go, 'verified');
    } catch (err) {
      summary = {
        verdict: 'verified',
        applied: [],
        conflicts: [],
        refused: [],
        auditPending: [],
        failed: go.map((r) => ({ id: r.id, status: null, reason: err instanceof Error ? err.message : String(err) })),
      };
    } finally {
      go.forEach((r) => setIn(setBusy, r.id, false));
    }
    for (const id of summary.applied) {
      const done = go.find((r) => r.id === id);
      if (done) applyLocal(token, id, verdictPatch(done, 'verified'));
      settle(id, 'verified');
    }
    markGiven(summary.applied);
    const skipped = toVerify.filter((r) => !go.includes(r)).map((r) => r.id);
    const keep = [
      ...summary.conflicts,
      ...summary.failed.map((f) => f.id),
      ...summary.refused.map((f) => f.id),
      ...skipped,
    ];
    setSelected(new Set(keep.map(rowKey)));

    const parts = [`${summary.applied.length} verified`];
    if (summary.conflicts.length) parts.push(`${summary.conflicts.length} changed by someone else`);
    if (summary.failed.length) parts.push(`${summary.failed.length} to retry`);
    const notDone = summary.refused.length + skipped.length;
    if (notDone) parts.push(`${notDone} can't be verified here`);
    const lines: string[] = [];
    for (const id of summary.conflicts) lines.push(`VER-${id} changed since it loaded. The table has been reloaded; it is still selected.`);
    for (const f of summary.failed) lines.push(`VER-${f.id} didn't save${f.status ? ` (${f.status})` : ''}. It is still selected, so Verify again retries it.`);
    for (const f of summary.refused) lines.push(`VER-${f.id}: ${refusalText(f.code)}`);
    for (const id of skipped) {
      const r = toVerify.find((x) => x.id === id);
      lines.push(`VER-${id}: ${r ? refusalFor(r) ?? 'not verified' : 'not verified'}`);
    }
    // Audit rows still owed are listed live from `owed` in the dialog (they can be posted there).
    for (const a of summary.auditPending) owe(a.id, a.drafts);
    if (summary.auditPending.length) console.warn('[verify] audit rows still to post', summary.auditPending);
    setBulk({ phase: 'done', summary, lines, headline: parts.join(' · ') });
    if (summary.conflicts.length) reload();
  };

  /** A plain click on a box sets the range anchor; a Shift-click sets every selectable row between. */
  const onCheckboxClick = (e: ReactMouseEvent, id: number) => {
    if (!e.shiftKey || anchor === null || anchor === id) {
      setAnchor(id);
      return;
    }
    const order = visible.map((r) => r.id);
    const a = order.indexOf(anchor);
    const b = order.indexOf(id);
    if (a < 0 || b < 0) {
      setAnchor(id);
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    const on = !selectedIds.includes(id);
    const ok = new Set(selectable);
    const next = new Set<Key>(selectedIds.map(rowKey));
    for (const rid of order.slice(Math.min(a, b), Math.max(a, b) + 1)) {
      if (!ok.has(rid)) continue;
      if (on) next.add(rowKey(rid));
      else next.delete(rowKey(rid));
    }
    setSelected(next);
    setAnchor(id);
  };

  // Row handlers: ONE stable object, so a memoised row re-renders only when its own props change.
  // It forwards to the latest closures, which the layout effect below refreshes after every render.
  const latest = useRef<RowHandlers | null>(null);
  useLayoutEffect(() => {
    latest.current = {
      verify: (row) => void commitOne(row, 'verified'),
      reject: (row, reason) => void commitOne(row, 'rejected', reason),
      toggleReject: (id) => setRejectFor((cur) => (cur === id ? null : id)),
      cancelReject: () => setRejectFor(null),
      toggleExpand: (id) =>
        setExpanded((prev) => {
          const next = new Set(prev);
          if (next.has(id)) next.delete(id);
          else next.add(id);
          return next;
        }),
      checkboxClick: onCheckboxClick,
    };
  });
  const handlers = useMemo<RowHandlers>(
    () => ({
      verify: (row) => latest.current?.verify(row),
      reject: (row, reason) => latest.current?.reject(row, reason),
      toggleReject: (id) => latest.current?.toggleReject(id),
      cancelReject: () => latest.current?.cancelReject(),
      toggleExpand: (id) => latest.current?.toggleExpand(id),
      checkboxClick: (e, id) => latest.current?.checkboxClick(e, id),
    }),
    [],
  );

  // ---- render ------------------------------------------------------------

  const head = (side: ReactNode) => (
    <div className={styles.head}>
      <div>
        <h1 className={styles.title}>Verify</h1>
        <p className={styles.lede}>
          Check what the agents wrote. Verify it, or reject it with a reason and it goes back to its owner.
          {actor && !actor.roles.includes('Oversight') ? ' These are the rows your role checks.' : ''}
        </p>
      </div>
      <div className={styles.side}>{side}</div>
    </div>
  );

  if (problem) {
    return (
      <div className={styles.page}>
        {head(null)}
        <div className={styles.notice}>
          <h2>The Q&amp;S site can't be reached.</h2>
          <p>{problem}. Reload the page to try again.</p>
        </div>
      </div>
    );
  }

  const closeSection = <IncidentsToClose result={toClose} actor={actor} />;

  if (!shown) {
    const failed = load?.status === 'error' ? load.message : null;
    return (
      <div className={styles.page}>
        {head(null)}
        {closeSection}
        <div className={styles.tsheet}>
          <div className={styles.empty}>
            {failed ? (
              <>
                <h2>The Verification Log couldn't be read.</h2>
                <p>{failed}</p>
                <Button className={`${styles.btn} ${styles.line}`} onPress={reload}>
                  Try again
                </Button>
              </>
            ) : (
              <p>Loading the Verification Log…</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  const filtered = (Object.keys(DEFAULT_FILTERS) as Array<keyof Filters>).some((k) => filters[k] !== DEFAULT_FILTERS[k]);
  const setFilter = <K extends keyof Filters>(k: K, v: Filters[K]) => {
    setFilters((f) => ({ ...f, [k]: v }));
    setSelected(new Set<Key>());
    setLimit(PAGE_SIZE);
  };

  const select = (k: keyof Filters, label: string, all: string, values: ReadonlyArray<readonly [string, string]>) => (
    <label className={styles.filter}>
      {label}
      <select value={filters[k]} onChange={(e) => setFilter(k, e.target.value as never)}>
        <option value="">{all}</option>
        {values.map(([v, text]) => (
          <option key={v} value={v}>
            {text}
          </option>
        ))}
      </select>
    </label>
  );

  const allSelected = selectable.length > 0 && selectedIds.length === selectable.length;
  const owedTotal = owedCount(owed);
  const bulkAuditIds = new Set(bulk?.phase === 'done' ? bulk.summary.auditPending.map((a) => a.id) : []);
  const bulkOwed = owedCount(owed, bulkAuditIds);


  return (
    <div className={styles.page}>
      {head(
        oversight ? (
          <>
            <b>{pendingCount}</b>pending, oldest first
          </>
        ) : (
          <>
            <b>{pendingCount}</b>waiting on your role
          </>
        ),
      )}

      {closeSection}

      {owedTotal > 0 ? (
        <div className={styles.owed} role="status" aria-live="polite">
          <p>
            <b>{plural(owedTotal, 'audit row')} still to post</b>, for {[...owed.keys()].map((id) => `VER-${id}`).join(', ')}. The
            verdicts saved, but the audit trail isn't complete until these post. Posting checks first, so nothing is written
            twice.
          </p>
          <Button className={`${styles.btn} ${styles.line} ${styles.sm}`} isDisabled={posting} onPress={() => void postOwed()}>
            {posting ? 'Posting…' : `Post the ${plural(owedTotal, 'audit row')}`}
          </Button>
        </div>
      ) : null}

      <div className={styles.filters} role="group" aria-label="Filters">
        <label className={styles.filter}>
          Verdict
          <select value={filters.verdict} onChange={(e) => setFilter('verdict', e.target.value as Filters['verdict'])}>
            {(['pending', 'verified', 'rejected', 'sampled-ok'] as const).map((v) => (
              <option key={v} value={v}>
                {VERDICT_LABEL[v]}
              </option>
            ))}
            <option value="all">All verdicts</option>
          </select>
        </label>
        {select('card', 'Card', 'All cards', options.card.map((v) => [v, v] as const))}
        {select('target', 'System', 'All systems', options.target.map((v) => [v, v] as const))}
        {select('action', 'What was written', 'All actions', options.action.map((v) => [v, v] as const))}
        {select('age', 'Age', 'Any age', [
          ['over', `Over ${E4_WINDOW_WORKING_DAYS} working days`],
          ['within', `Within ${E4_WINDOW_WORKING_DAYS} working days`],
        ])}
        <p className={styles.showing} aria-live="polite">
          {visible.length === rows.length ? `${rows.length} rows` : `Showing ${visible.length} of ${rows.length}`}
          {base ? null : ', reloading…'}
        </p>
      </div>

      <div className={styles.tsheet}>
        <Table
          aria-label="Verification Log rows"
          className={styles.table}
          selectionMode="multiple"
          selectionBehavior="toggle"
          selectedKeys={selected}
          onSelectionChange={(keys) => setSelected(keys === 'all' ? new Set(selectable.map(rowKey)) : keys)}
          disabledKeys={disabledKeys}
          disabledBehavior="selection"
        >
          <TableHeader>
            <Column className={styles.ck}>
              <Checkbox slot="selection" className={styles.check} aria-label={`Select all ${selectable.length} rows that can be verified, including any not yet shown`}>
                {({ isSelected, isIndeterminate }) => <Box on={isSelected || allSelected} mixed={isIndeterminate} />}
              </Checkbox>
            </Column>
            <Column isRowHeader className={styles.colCard}>
              Check
            </Column>
            <Column className={styles.colRecord}>Record</Column>
            <Column className={styles.colWrote}>What the agent wrote</Column>
            <Column className={styles.colWhen}>Written</Column>
            <Column className={`${styles.num} ${styles.colAge}`}>
              <span title={`Working days since it was written. Over ${E4_WINDOW_WORKING_DAYS} is past E4's window.`}>Age</span>
            </Column>
            <Column className={styles.acts}>
              <span className={styles.sr}>Actions</span>
            </Column>
          </TableHeader>
          <TableBody
            renderEmptyState={() => (
              <div className={styles.empty}>
                {filters.verdict === 'pending' && !filtered ? (
                  <>
                    <h2>Nothing waiting for you to verify.</h2>
                    <p>
                      {oversight
                        ? 'When an agent writes something to check, it lands here.'
                        : 'When an agent writes something your role checks, it lands here.'}
                    </p>
                  </>
                ) : (
                  <>
                    <h2>No rows match these filters.</h2>
                    <Button className={`${styles.btn} ${styles.line}`} onPress={() => setFilters(DEFAULT_FILTERS)}>
                      Clear the filters
                    </Button>
                  </>
                )}
              </div>
            )}
          >
            {onScreen.map((r) => (
              <VerifyRow
                key={r.id}
                row={r}
                link={links.get(r.id) ?? NO_LINK}
                refusal={refusals.get(r.id) ?? null}
                now={now}
                canAct={actor !== null}
                busy={busy.has(r.id)}
                done={settled.get(r.id)}
                leaving={leaving.has(r.id)}
                expanded={expanded.has(r.id)}
                rejectOpen={rejectFor === r.id}
                note={notes.get(r.id)}
                windowClosed={windowClosed.has(r.id)}
                on={handlers}
              />
            ))}
          </TableBody>
        </Table>
      </div>

      {visible.length > onScreen.length ? (
        <div className={styles.more}>
          <Button className={`${styles.btn} ${styles.line}`} onPress={() => setLimit((n) => n + PAGE_SIZE)}>
            Show the next {Math.min(PAGE_SIZE, visible.length - onScreen.length)}
          </Button>
          <span>
            {onScreen.length} of {visible.length} shown, oldest first
          </span>
        </div>
      ) : null}

      {doneToday.length > 0 ? (
        <section className={styles.doneToday} aria-labelledby="vdone-h">
          <h2 id="vdone-h">Done today</h2>
          <p className={styles.doneLede}>
            {oversight
              ? 'Verdicts given today. A mistake can be corrected until the end of the day, with a reason.'
              : 'Your verdicts today. A mistake can be corrected until the end of the day, with a reason.'}
          </p>
          <ul className={styles.doneList}>
            {doneToday.map(({ row, why }) => (
              <li key={row.id} className={styles.doneItem}>
                <div className={styles.doneMain}>
                  <span className={`${styles.tag} ${row.verdict === 'rejected' ? styles.tagRejected : ''}`}>
                    {VERDICT_LABEL[row.verdict]}
                  </span>
                  <span className={styles.mono}>VER-{row.id}</span>
                  <span className={styles.doneWhat}>
                    {(row.cardId || 'No card').replace(/-/g, '\u2011')}, {row.recordRef || 'no record ref'}
                  </span>
                  <time className={styles.when} dateTime={row.verifiedAt ?? undefined}>
                    {absolute(row.verifiedAt)}
                  </time>
                  {why === null && correctFor !== row.id ? (
                    <Button
                      className={`${styles.btn} ${styles.line} ${styles.sm}`}
                      isDisabled={busy.has(row.id)}
                      onPress={() => setCorrectFor(row.id)}
                    >
                      Correct this
                    </Button>
                  ) : null}
                </div>
                {why ? <p className={styles.refusal}>{why}</p> : null}
                {notes.get(row.id) ? (
                  <p className={styles.rowNote} role="status">
                    {notes.get(row.id)}
                  </p>
                ) : null}
                {correctFor === row.id && why === null ? (
                  <CorrectComposer
                    row={row}
                    busy={busy.has(row.id)}
                    onCancel={() => setCorrectFor(null)}
                    onSubmit={(verdict, reason) => void commitCorrect(row, verdict, reason)}
                  />
                ) : null}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {selectedIds.length > 0 ? (
        <div className={styles.bulkbar} role="region" aria-label="Selection">
          <span>{selectedIds.length} selected</span>
          <span className={styles.aside}>Shift-click selects a range</span>
          <Button className={`${styles.btn} ${styles.quiet} ${styles.sm} ${styles.onDark}`} onPress={() => setSelected(new Set<Key>())}>
            Clear
          </Button>
          <Button className={`${styles.btn} ${styles.go}`} onPress={openBulk}>
            Verify {selectedIds.length}
          </Button>
        </div>
      ) : null}

      <ModalOverlay
        className={styles.overlay}
        isOpen={bulk !== null}
        isDismissable={bulk?.phase !== 'running'}
        isKeyboardDismissDisabled={bulk?.phase === 'running'}
        onOpenChange={(isOpen) => {
          if (!isOpen && bulk?.phase !== 'running') setBulk(null);
        }}
      >
        <Modal className={styles.modal}>
          <Dialog className={styles.dialog} role={bulk?.phase === 'confirm' ? 'alertdialog' : 'dialog'}>
            {bulk?.phase === 'confirm' ? (
              <BulkConfirm
                rows={bulk.rows}
                as={actor ? roleLabel(actor.roles) : 'Oversight'}
                onCancel={() => setBulk(null)}
                onConfirm={() => void runBulk(bulk.rows)}
              />
            ) : bulk?.phase === 'running' ? (
              <div className={styles.cb}>
                <Heading slot="title">Verifying {plural(bulk.count, 'row')}…</Heading>
                <div className={styles.progress} aria-hidden="true">
                  <i />
                </div>
                <p className={styles.muted}>One row at a time, each checked that nobody else changed it first.</p>
              </div>
            ) : bulk?.phase === 'done' ? (
              <>
                <div className={styles.cb}>
                  <Heading slot="title">Bulk verify finished</Heading>
                  <p className={styles.result}>
                    {bulk.headline}
                    {bulkOwed ? ` · ${plural(bulkOwed, 'audit row')} still to post` : ''}
                  </p>
                  {bulk.lines.length || bulkAuditIds.size ? (
                    <ul className={styles.exlist}>
                      {bulk.lines.map((l) => (
                        <li key={l}>{l}</li>
                      ))}
                      {[...bulkAuditIds].map((id) => {
                        const n = owed.get(id)?.length ?? 0;
                        return n ? (
                          <li key={`a${id}`}>
                            VER-{id} saved, but {plural(n, 'audit row')} didn't post. Post them before you close this.
                          </li>
                        ) : null;
                      })}
                      {bulkAuditIds.size && !bulkOwed ? <li key="a-done">Every audit row is now posted.</li> : null}
                    </ul>
                  ) : null}
                </div>
                <div className={styles.cf}>
                  {bulkOwed ? (
                    <Button className={`${styles.btn} ${styles.line}`} isDisabled={posting} onPress={() => void postOwed()}>
                      {posting ? 'Posting…' : `Post the ${plural(bulkOwed, 'audit row')}`}
                    </Button>
                  ) : null}
                  <Button className={`${styles.btn} ${styles.go}`} slot="close" isDisabled={posting} autoFocus>
                    Close
                  </Button>
                </div>
              </>
            ) : null}
          </Dialog>
        </Modal>
      </ModalOverlay>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pieces
// ---------------------------------------------------------------------------

interface RowHandlers {
  verify: (row: VerificationRow) => void;
  reject: (row: VerificationRow, reason: string) => void;
  toggleReject: (id: number) => void;
  cancelReject: () => void;
  toggleExpand: (id: number) => void;
  checkboxClick: (e: ReactMouseEvent, id: number) => void;
}

interface RowProps {
  row: VerificationRow;
  link: LinkedState;
  /** Why it can't be verified / rejected here (said, never hidden), or null. */
  refusal: string | null;
  now: number;
  canAct: boolean;
  busy: boolean;
  done: Settled | undefined;
  leaving: boolean;
  expanded: boolean;
  rejectOpen: boolean;
  note: string | undefined;
  /** It has a verdict and its correction day has passed (D-13-R-5). */
  windowClosed: boolean;
  on: RowHandlers;
}

/** One Verification Log row, plus its expanded row (reject composer / details) when open. */
const VerifyRow = memo(function VerifyRow({
  row: r,
  link,
  refusal,
  now,
  canAct,
  busy,
  done,
  leaving,
  expanded,
  rejectOpen,
  note,
  windowClosed,
  on,
}: RowProps) {
  const parsed = parseRecordRef(r.recordRef);
  const q = vlQuestion(r.action);
  const wd = workingDaysSince(r.writtenAt, now);
  const old = wd !== null && wd > E4_WINDOW_WORKING_DAYS && r.verdict === 'pending';
  const refusalId = `vref-${r.id}`;
  const actionable = canAct && !busy && refusal === null;
  return (
    <>
      <Row id={rowKey(r.id)} className={`${styles.row} ${leaving ? styles.leaving : ''}`}>
        <Cell className={styles.ck}>
          {r.verdict === 'pending' && !done ? (
            // Shift-click on the box selects a range too (the row already does).
            <span onClickCapture={(e) => on.checkboxClick(e, r.id)}>
              <Checkbox slot="selection" className={styles.check} aria-label={`Select VER-${r.id}`}>
                {({ isSelected, isDisabled }) => <Box on={isSelected} off={isDisabled} />}
              </Checkbox>
            </span>
          ) : null}
        </Cell>
        <Cell>
          {/* The question first (13.1-14, ISS-241): the agent's card id and verb are in the details. */}
          <b className={styles.question} title={q.ask}>
            {q.headline}
          </b>
          <div className={`${styles.sub} ${styles.mono}`}>VER-{r.id}</div>
        </Cell>
        <Cell>
          {parsed.kind === 'incident' ? (
            <Link className={styles.ref} to={`/trail?ref=${encodeURIComponent(parsed.ref)}`}>
              {r.recordRef}
            </Link>
          ) : parsed.kind === 'action' ? null : (
            <span className={styles.mono}>{r.recordRef || 'No record ref'}</span>
          )}
          <LinkedAction link={link} />
          {refusal ? (
            <p id={refusalId} className={styles.refusal}>
              {refusal}
            </p>
          ) : null}
          {note ? (
            <p className={styles.rowNote} role="status">
              {note}
            </p>
          ) : null}
        </Cell>
        <Cell>
          {q.label}
          <div className={styles.sub}>{r.cardId ? `Agent card ${r.cardId.replace(/-/g, '‑')}` : 'No agent card'}</div>
        </Cell>
        <Cell>
          <time className={styles.when} dateTime={r.writtenAt} title={absolute(r.writtenAt)}>
            {relative(r.writtenAt, now)}
          </time>
        </Cell>
        <Cell className={`${styles.num} ${old ? styles.old : ''}`}>
          {wd === null ? 'unknown' : wd === 0 ? 'today' : plural(wd, 'day')}
        </Cell>
        <Cell className={styles.acts}>
          {done ? (
            <span className={styles.tick} role="status">
              <TickIcon />
              {done === 'verified' ? 'Verified' : 'Rejected'}
            </span>
          ) : r.verdict === 'pending' ? (
            <>
              <Button
                className={`${styles.btn} ${styles.go} ${styles.sm}`}
                isDisabled={!actionable}
                aria-describedby={refusal ? refusalId : undefined}
                onPress={() => on.verify(r)}
              >
                {busy ? 'Saving…' : 'Verify'}
              </Button>
              <Button
                className={`${styles.btn} ${styles.quiet} ${styles.sm}`}
                isDisabled={!actionable}
                aria-describedby={refusal ? refusalId : undefined}
                aria-expanded={rejectOpen}
                onPress={() => on.toggleReject(r.id)}
              >
                Reject
              </Button>
            </>
          ) : (
            <span className={`${styles.tag} ${r.verdict === 'rejected' ? styles.tagRejected : ''}`}>
              {VERDICT_LABEL[r.verdict]}
            </span>
          )}
          <Button
            className={styles.chev}
            aria-label={`${expanded ? 'Hide' : 'Show'} the details of VER-${r.id}`}
            aria-expanded={expanded}
            onPress={() => on.toggleExpand(r.id)}
          >
            <ChevronIcon open={expanded} />
          </Button>
        </Cell>
      </Row>
      {expanded || rejectOpen ? (
        <Row id={detailKey(r.id)} className={styles.detailRow}>
          <Cell className={styles.ck}>{null}</Cell>
          <Cell colSpan={6}>
            {rejectOpen ? (
              <RejectComposer
                id={r.id}
                busy={busy}
                onCancel={on.cancelReject}
                onSubmit={(reason) => on.reject(r, reason)}
              />
            ) : null}
            {expanded ? <Details row={r} link={link} windowClosed={windowClosed} /> : null}
          </Cell>
        </Row>
      ) : null}
    </>
  );
});

function BulkConfirm({
  rows,
  as,
  onCancel,
  onConfirm,
}: {
  rows: VerificationRow[];
  /** The role the verdicts are given in (the headline role label). */
  as: string;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const byCard = new Map<string, number>();
  for (const r of rows) byCard.set(r.cardId || 'No card', (byCard.get(r.cardId || 'No card') ?? 0) + 1);
  const breakdown = [...byCard.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  const ids = rows.map((r) => `VER-${r.id}`);
  const SHOW = 24;
  return (
    <>
      <div className={styles.cb}>
        <Heading slot="title">
          Verify {plural(rows.length, 'row')} as {as}?
        </Heading>
        <p>Each row records you as the verifier, with today's date and time.</p>
        <ul className={styles.breakdown} aria-label="Rows by card">
          {breakdown.map(([card, n]) => (
            <li key={card}>
              <b>{n}</b> from {card}
            </li>
          ))}
        </ul>
        <p className={styles.muted}>
          {ids.slice(0, SHOW).join(', ')}
          {ids.length > SHOW ? `, and ${ids.length - SHOW} more` : ''}
        </p>
      </div>
      <div className={styles.cf}>
        <Button className={`${styles.btn} ${styles.quiet}`} onPress={onCancel}>
          Cancel
        </Button>
        <Button className={`${styles.btn} ${styles.go}`} onPress={onConfirm} autoFocus>
          Verify {rows.length}
        </Button>
      </div>
    </>
  );
}

interface ComposerProps {
  id: number;
  busy: boolean;
  onCancel: () => void;
  /** Called with the trimmed reason. */
  onSubmit: (reason: string) => void;
}

/** Holds its own text, so typing re-renders the field, not the whole table. */
function RejectComposer({ id, busy, onCancel, onSubmit }: ComposerProps) {
  const [value, setValue] = useState('');
  const length = value.trim().length;
  const submit = () => onSubmit(value.trim());
  const ready = length >= MIN_REASON_CHARS && !busy;
  const inputId = `vrj-${id}`;
  return (
    // The table's keyboard handling (arrows, Space, type-ahead) must not take keys from the field.
    <div
      className={styles.reject}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onCancel();
        e.stopPropagation();
      }}
    >
      <label htmlFor={inputId} className={styles.rejectLabel}>
        Why are you rejecting VER-{id}? It goes back to its owner with this reason.
      </label>
      <div className={styles.field}>
        <input
          id={inputId}
          className={styles.input}
          value={value}
          placeholder="Required"
          autoFocus
          aria-describedby={`${inputId}-hint`}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && ready) submit();
          }}
        />
        <Button className={`${styles.btn} ${styles.line} ${styles.sm}`} isDisabled={!ready} onPress={submit}>
          {busy ? 'Saving…' : 'Reject and return'}
        </Button>
        <Button className={`${styles.btn} ${styles.quiet} ${styles.sm}`} onPress={onCancel}>
          Cancel
        </Button>
      </div>
      <p id={`${inputId}-hint`} className={styles.hint}>
        {length > 0 && length < MIN_REASON_CHARS
          ? `${MIN_REASON_CHARS - length} more characters needed. `
          : ''}
        Reject one row at a time; each needs its own reason. A second rejection is also sent to the owner's manager.
      </p>
    </div>
  );
}

interface CorrectProps {
  row: VerificationRow;
  busy: boolean;
  onCancel: () => void;
  /** Called with the new verdict and the trimmed reason. */
  onSubmit: (verdict: VlCorrectVerdict, reason: string) => void;
}

/** "Correct this": the opposite verdict, with a required reason. Holds its own text. */
function CorrectComposer({ row, busy, onCancel, onSubmit }: CorrectProps) {
  const [value, setValue] = useState('');
  const to: VlCorrectVerdict = row.verdict === 'rejected' ? 'verified' : 'rejected';
  const from = VERDICT_LABEL[row.verdict];
  const toLabel = VERDICT_LABEL[to];
  const length = value.trim().length;
  const ready = length >= MIN_REASON_CHARS && !busy;
  const submit = () => onSubmit(to, value.trim());
  const inputId = `vcr-${row.id}`;
  return (
    <div
      className={`${styles.reject} ${styles.correct}`}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onCancel();
      }}
    >
      <label htmlFor={inputId} className={styles.rejectLabel}>
        Change VER-{row.id} from {from} to {toLabel}? Say why; it is kept in the audit trail.
      </label>
      <div className={styles.field}>
        <input
          id={inputId}
          className={styles.input}
          value={value}
          placeholder="Required"
          autoFocus
          aria-describedby={`${inputId}-hint`}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && ready) submit();
          }}
        />
        <Button className={`${styles.btn} ${styles.line} ${styles.sm}`} isDisabled={!ready} onPress={submit}>
          {busy ? 'Saving…' : `Change to ${toLabel}`}
        </Button>
        <Button className={`${styles.btn} ${styles.quiet} ${styles.sm}`} onPress={onCancel}>
          Cancel
        </Button>
      </div>
      <p id={`${inputId}-hint`} className={styles.hint}>
        {length > 0 && length < MIN_REASON_CHARS ? `${MIN_REASON_CHARS - length} more characters needed. ` : ''}
        Corrections are open until the end of the day the verdict was given.
      </p>
    </div>
  );
}

function Details({ row, link, windowClosed }: { row: VerificationRow; link: LinkedState; windowClosed: boolean }) {
  const parsed = parseRecordRef(row.recordRef);
  const q = vlQuestion(row.action);
  return (
    <div className={styles.details}>
      <dl className={styles.facts}>
        <dt>What you're checking</dt>
        <dd>{q.ask}</dd>
      </dl>
      {parsed.kind === 'action' ? <LinkedAction link={link} detail /> : null}
      <dl className={styles.facts}>
        <dt>Agent card</dt>
        <dd>{row.cardId || 'None'}</dd>
        <dt>Agent verb</dt>
        <dd className={styles.mono}>{row.action || 'None'}</dd>
        <dt>Record</dt>
        <dd className={styles.mono}>{row.recordRef || 'None'}</dd>
        <dt>Target</dt>
        <dd>{row.targetSystem || 'None'}</dd>
        <dt>Title</dt>
        <dd>{row.title || 'None'}</dd>
        <dt>Step</dt>
        <dd>{row.step ?? 'None'}</dd>
        <dt>Idempotency key</dt>
        <dd className={styles.mono}>{row.idempotencyKey ?? 'None'}</dd>
        <dt>Payload hash</dt>
        <dd className={`${styles.mono} ${styles.hash}`}>{row.payloadHash ?? 'None'}</dd>
        <dt>Written</dt>
        <dd>{absolute(row.writtenAt) || 'Unknown'}</dd>
        <dt>Written by</dt>
        <dd>{row.agentIdentity || 'Unknown'}</dd>
        {row.verdict !== 'pending' ? (
          <>
            <dt>Verdict</dt>
            <dd>
              {VERDICT_LABEL[row.verdict]}
              {row.verifiedAt ? `, ${absolute(row.verifiedAt)}` : ''}
              {row.verifierRole ? `, as ${row.verifierRole}` : ''}
            </dd>
          </>
        ) : null}
        {row.exceptionReason ? (
          <>
            <dt>Reason</dt>
            <dd>{row.exceptionReason}</dd>
          </>
        ) : null}
        {windowClosed ? (
          <>
            <dt>Correcting it</dt>
            <dd>{refusalText('outside-window')}</dd>
          </>
        ) : null}
      </dl>
      {parsed.kind === 'incident' ? (
        <p className={styles.trail}>
          <Link to={`/trail?ref=${encodeURIComponent(parsed.ref)}`}>Open the closure trail for {parsed.ref}</Link>
        </p>
      ) : null}
    </div>
  );
}

function Box({ on, mixed = false, off = false }: { on: boolean; mixed?: boolean; off?: boolean }) {
  return (
    <span className={styles.box} data-on={on || mixed || undefined} data-off={off || undefined} aria-hidden="true">
      {mixed && !on ? (
        <svg viewBox="0 0 16 16">
          <path d="M4 8h8" />
        </svg>
      ) : on ? (
        <svg viewBox="0 0 16 16">
          <path d="M3.5 8.5l3 3 6-7" />
        </svg>
      ) : null}
    </span>
  );
}

function TickIcon() {
  return (
    <svg className={styles.tickIcon} viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
      <path d="M7.5 12.5l3 3 6-7" />
    </svg>
  );
}

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className={open ? styles.chevOpen : undefined}>
      <path d="M6 9l6 6 6-6" />
    </svg>
  );
}
